import {skipToken} from '@reduxjs/toolkit/query'
import * as React from 'react'

import {useLocator} from '../../../../../contexts/locator'
import {useBuildId} from '../../../../../hooks/routes'
import {getBuildTypesArg, getDependantsLocator} from '../../../../../rest/buildTypes'
import {
  getBuild,
  getBuildsReady,
  getSnapshotDependencies,
  makeGetBuilds,
} from '../../../../../selectors'
import type {BuildType} from '../../../../../services/rest'
import {restApi} from '../../../../../services/rest'
import type {BuildTypeId, NormalizedBuild} from '../../../../../types'
import {emptyArray, getEmptyHash} from '../../../../../utils/empty'
import {notNull} from '../../../../../utils/guards'
import type {KeyValue, WritableKeyValue} from '../../../../../utils/object'

import {useAppSelector} from 'src/hooks/react-redux'

type UseChainReturn = {
  self: NormalizedBuild | null | undefined
  ready: boolean
  allBuilds: ReadonlyArray<NormalizedBuild>
  buildsByBuildType: KeyValue<BuildTypeId, ReadonlyArray<NormalizedBuild>>
  buildTypesHash: KeyValue<BuildTypeId, BuildType>
  buildTypes: ReadonlyArray<BuildType>
}
export function useChain(): UseChainReturn {
  const buildId = useBuildId()
  const self = useAppSelector(state => getBuild(state, buildId))
  const selfBuildTypeId = self?.buildType
  const locator = useLocator()
  const buildsReady = useAppSelector(state => self != null && getBuildsReady(state, locator))
  const {buildTypesNormalized, buildTypesReady} =
    restApi.endpoints.getAllBuildTypesNormalized.useQuery(
      selfBuildTypeId != null
        ? getBuildTypesArg(getDependantsLocator(selfBuildTypeId), {withSnapshotDependencies: true})
        : skipToken,
      {
        selectFromResult: ({data}) => ({
          buildTypesNormalized: data,
          buildTypesReady: data != null,
        }),
      },
    )
  const selfReady = useAppSelector(state => getSnapshotDependencies(state)[buildId!] != null)
  const ready = buildsReady && buildTypesReady && selfReady
  const getBuilds = React.useMemo(makeGetBuilds, [])
  const builds = useAppSelector(state => (ready ? getBuilds(state, locator) : emptyArray))
  const allBuilds: ReadonlyArray<NormalizedBuild> = React.useMemo(
    () => (ready && self != null ? [self, ...builds] : emptyArray),
    [ready, self, builds],
  )
  const buildsByBuildType: KeyValue<
    BuildTypeId,
    ReadonlyArray<NormalizedBuild>
  > = React.useMemo(() => {
    const result: WritableKeyValue<BuildTypeId, Array<NormalizedBuild>> = {}

    for (const build of allBuilds) {
      const buildTypeId = build.buildType
      let buildTypeBuilds = result[buildTypeId]

      if (buildTypeBuilds == null) {
        buildTypeBuilds = result[buildTypeId] = []
      }

      buildTypeBuilds.push(build)
    }

    return result
  }, [allBuilds])
  const buildTypesHash: KeyValue<BuildTypeId, BuildType> = useAppSelector(state =>
    ready ? state.entities.buildTypes : getEmptyHash(),
  )
  const buildTypes = React.useMemo(
    () =>
      buildTypesNormalized?.result
        .map(id => buildTypesNormalized.entities.buildTypes?.[id])
        .filter(notNull) ?? emptyArray,
    [buildTypesNormalized],
  )
  return {
    self,
    ready,
    allBuilds,
    buildsByBuildType,
    buildTypesHash,
    buildTypes,
  }
}
